import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { PieChart, Pie, Cell, ResponsiveContainer, Sector } from 'recharts';
import TextMarquee from 'components/TextMarquee';

export type MostPlayedDonutSlice = {
  /** Raw id / mnum from API (or "Other"). */
  id: string;
  /** Display name (resolved game title, or id fallback). */
  name: string;
  /** Percentage of total spins (0–100). */
  value: number;
  spins: number;
  color: string;
};

type Props = {
  slices: MostPlayedDonutSlice[];
  totalSpins: number;
};

const fmtSpins = (n: number): string => {
  if (!Number.isFinite(n)) return '0';
  return Math.round(n).toLocaleString('en-US');
};

const fmtPct = (value: number): string => {
  if (!Number.isFinite(value)) return '0%';
  return `${value}%`;
};

const POP_OFFSET = 10;
const POP_GROW = 6;
const POP_MS = 200;
const CHART_HEIGHT = 260;

const usePrefersReducedMotion = (): boolean => {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduced(mq.matches);
    update();
    if (mq.addEventListener) mq.addEventListener('change', update);
    else mq.addListener(update);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', update);
      else mq.removeListener(update);
    };
  }, []);
  return reduced;
};

/** Scale font-size so the primary number always fits inside the donut hole. */
const FitSpinCount: React.FC<{
  text: string;
  maxFontPx: number;
  minFontPx: number;
  maxWidthPx: number;
}> = ({ text, maxFontPx, minFontPx, maxWidthPx }) => {
  const ref = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState(maxFontPx);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || maxWidthPx <= 0) return;
    let size = maxFontPx;
    el.style.fontSize = `${size}px`;
    while (size > minFontPx && el.scrollWidth > maxWidthPx) {
      size -= 1;
      el.style.fontSize = `${size}px`;
    }
    setFontSize(size);
  }, [text, maxFontPx, minFontPx, maxWidthPx]);

  return (
    <span ref={ref} className="most-played-donut__total" style={{ fontSize }}>
      {text}
    </span>
  );
};

type ActiveShapeProps = {
  cx: number;
  cy: number;
  innerRadius: number;
  outerRadius: number;
  startAngle: number;
  endAngle: number;
  fill: string;
  fillOpacity?: number;
  reducedMotion: boolean;
};

const PopSector: React.FC<ActiveShapeProps> = ({
  cx,
  cy,
  innerRadius,
  outerRadius,
  startAngle,
  endAngle,
  fill,
  fillOpacity = 1,
  reducedMotion,
}) => {
  const RADIAN = Math.PI / 180;
  const mid = (startAngle + endAngle) / 2;
  const ox = Math.cos(-mid * RADIAN) * POP_OFFSET;
  const oy = Math.sin(-mid * RADIAN) * POP_OFFSET;
  return (
    <g
      style={{
        transition: reducedMotion ? 'none' : `transform ${POP_MS}ms ease`,
        transform: `translate(${ox}px, ${oy}px)`,
      }}
    >
      <Sector
        cx={cx}
        cy={cy}
        innerRadius={innerRadius}
        outerRadius={outerRadius + POP_GROW}
        startAngle={startAngle}
        endAngle={endAngle}
        fill={fill}
        fillOpacity={fillOpacity}
        strokeWidth={0}
      />
    </g>
  );
};

/**
 * Recharts `ActiveShape` accepts `(props: unknown) => JSX.Element`.
 * Narrow sector fields inside so we stay assignable without `any`.
 */
const makePopActiveShape = (
  fillOpacity: number,
  reducedMotion: boolean
): ((props: unknown) => JSX.Element) => {
  function PopActiveShape(props: unknown): JSX.Element {
    const raw =
      props !== null && typeof props === 'object'
        ? (props as Record<string, unknown>)
        : {};
    const n = (key: string): number => {
      const v = raw[key];
      return typeof v === 'number' && Number.isFinite(v) ? v : 0;
    };
    const fill = typeof raw.fill === 'string' ? raw.fill : '#ffffff';
    return (
      <PopSector
        cx={n('cx')}
        cy={n('cy')}
        innerRadius={n('innerRadius')}
        outerRadius={n('outerRadius')}
        startAngle={n('startAngle')}
        endAngle={n('endAngle')}
        fill={fill}
        fillOpacity={fillOpacity}
        reducedMotion={reducedMotion}
      />
    );
  }
  return PopActiveShape;
};

const MostPlayedTitlesDonut: React.FC<Props> = ({ slices, totalSpins }) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);
  const [chartWidth, setChartWidth] = useState(320);
  const chartRef = useRef<HTMLDivElement>(null);
  const reducedMotion = usePrefersReducedMotion();

  useLayoutEffect(() => {
    const node = chartRef.current;
    if (!node || typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect?.width;
      if (typeof w === 'number' && w > 0) {
        setChartWidth((prev) => (Math.abs(prev - w) < 0.5 ? prev : w));
      }
    });
    ro.observe(node);
    setChartWidth(node.clientWidth || 320);
    return () => ro.disconnect();
  }, []);

  const sliceKey = useMemo(
    () => slices.map((s) => `${s.id}:${s.spins}`).join('|'),
    [slices]
  );
  useEffect(() => {
    setSelectedIndex(null);
  }, [sliceKey]);

  const radii = useMemo(() => {
    const side = Math.min(chartWidth, CHART_HEIGHT);
    // Leave room so a popped slice does not clip.
    const outerRadius = Math.max(70, Math.min(100, side * 0.34));
    const innerRadius = outerRadius * 0.82;
    const holeRadius = innerRadius * 0.9;
    return { outerRadius, innerRadius, holeRadius };
  }, [chartWidth]);

  // Nice-to-have: while selected, center shows that game's spins (same unit as the
  // default total). Percentage stays on the legend pill so the hole stays readable.
  const selected = selectedIndex != null ? slices[selectedIndex] : null;
  const centerText = fmtSpins(selected ? selected.spins : totalSpins);

  const holePad = 12;
  const fitWidth = Math.max(40, radii.holeRadius * 2 - holePad * 2);
  const maxFont = Math.min(48, fitWidth * 0.4);
  const minFont = 12;

  const toggleIndex = useCallback((index: number) => {
    setSelectedIndex((prev) => (prev === index ? null : index));
  }, []);

  const onChartBackgroundClick = useCallback(() => {
    setSelectedIndex(null);
  }, []);

  const activeIndex = selectedIndex == null ? -1 : selectedIndex;

  const shadeActiveShape = useMemo(
    () => makePopActiveShape(0.1, reducedMotion),
    [reducedMotion]
  );
  const ringActiveShape = useMemo(
    () => makePopActiveShape(1, reducedMotion),
    [reducedMotion]
  );

  if (!slices.length) return null;

  return (
    <div className="most-played-donut">
      <div
        ref={chartRef}
        className="most-played-donut__chart"
        onClick={onChartBackgroundClick}
        role="presentation"
      >
        <ResponsiveContainer width="100%" height={CHART_HEIGHT}>
          <PieChart margin={{ top: 18, right: 18, bottom: 18, left: 18 }}>
            {/* Translucent fill — decorative only; clicks handled by the ring pie. */}
            <Pie
              data={slices}
              cx="50%"
              cy="50%"
              innerRadius={0}
              outerRadius={radii.outerRadius - 8}
              paddingAngle={3}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              labelLine={false}
              isAnimationActive={false}
              activeIndex={activeIndex}
              activeShape={shadeActiveShape}
              style={{ pointerEvents: 'none' }}
            >
              {slices.map((entry, index) => (
                <Cell
                  key={`shade-${entry.id}-${index}`}
                  fill={entry.color}
                  fillOpacity={0.1}
                  strokeWidth={0}
                />
              ))}
            </Pie>
            <Pie
              data={slices}
              cx="50%"
              cy="50%"
              innerRadius={radii.innerRadius}
              outerRadius={radii.outerRadius}
              paddingAngle={3}
              dataKey="value"
              startAngle={90}
              endAngle={-270}
              labelLine={false}
              isAnimationActive={false}
              activeIndex={activeIndex}
              activeShape={ringActiveShape}
              onClick={(_, index, e) => {
                if (e && typeof (e as { stopPropagation?: () => void }).stopPropagation === 'function') {
                  (e as { stopPropagation: () => void }).stopPropagation();
                }
                toggleIndex(index);
              }}
              style={{ cursor: 'pointer' }}
            >
              {slices.map((entry, index) => (
                <Cell key={`cell-${entry.id}-${index}`} fill={entry.color} strokeWidth={0} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>

        {/* Hole fill via DOM (avoids recharts Customized TS friction); sits under center text. */}
        <div
          className="most-played-donut__hole"
          style={{
            width: radii.holeRadius * 2,
            height: radii.holeRadius * 2,
          }}
          aria-hidden="true"
        />

        <div
          className="most-played-donut__center"
          style={{
            width: fitWidth,
            maxWidth: fitWidth,
          }}
          aria-live="polite"
        >
          <FitSpinCount
            text={centerText}
            maxFontPx={maxFont}
            minFontPx={minFont}
            maxWidthPx={fitWidth}
          />
          <span className="most-played-donut__label">Spins</span>
        </div>
      </div>

      <ul className="most-played-donut__legend">
        {slices.map((entry, index) => {
          const pressed = selectedIndex === index;
          const pct = fmtPct(entry.value);
          return (
            <li key={`${entry.id}-${index}`} className="most-played-donut__legend-item">
              <button
                type="button"
                className={`most-played-donut__legend-btn${
                  pressed ? ' most-played-donut__legend-btn--selected' : ''
                }`}
                aria-pressed={pressed}
                aria-label={`${entry.name}, ${pct}`}
                onClick={() => toggleIndex(index)}
              >
                <span
                  className="most-played-donut__swatch"
                  style={{ backgroundColor: entry.color }}
                  aria-hidden="true"
                />
                <span className="most-played-donut__legend-pill">
                  <TextMarquee className="most-played-donut__legend-name" text={entry.name} />
                  <span className="most-played-donut__legend-pct"> | {pct}</span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default MostPlayedTitlesDonut;
